import { Box, Paper, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useIsMobile } from "../../hook/useIsMobile";
import UpdateGatewayForm from "../../components/forms/toolsForm/updateGatewayForm.component";
import FormActionButton from "../../components/buttons/formActionButton.component";
import { Delete, MapOutlined } from "@mui/icons-material";
import SubmitButton from "../../components/buttons/submitButton.component";
import { useForm } from "react-hook-form";
import { useCallback, useEffect, useState } from "react";
import UpdateSensorForm from "../../components/forms/toolsForm/updateSensorForm.component";
import { useDispatch, useSelector } from "react-redux";
import { fetchFieldsStart } from "../../store/features/customer/fields/fieldsSlice";
import LinkButton from "../../components/buttons/linkButton.component";
import CustomDialog from "../../components/muiDialogs/dialog.component";
import { dialogMessages } from "../../components/forms/utils";
import {
  deleteSensorStart,
  resetDeleteSensorState,
  resetUpdateSensorState,
  updateSensorStart,
} from "../../store/features/customer/sensor/sensorSlice";
import CustomAlert from "../../components/alert/alert.component";
import {
  deleteGatewayStart,
  resetDeleteGatewayState,
  resetUpdateGatewayState,
  updateGatewayStart,
} from "../../store/features/customer/gateway/gatewaySlice";
import { fetchUpdateFrequenciesStart } from "../../store/features/customer/updateFrequencies/updateFrequenciesSlice";

import { fetchSensorTypesStart } from "../../store/features/customer/sensorTypes/sensorTypesSlice";
import { fetchSensorsStart } from "../../store/features/customer/instruments/sensorsSlice";
import { getErrorMessage } from "../../utils/errorUtils";

const getAssignedFieldId = (tool) =>
  tool?.assigned_field?.id || tool?.field?.id || tool?.field || "";

const isSameId = (left, right) => String(left || "") === String(right || "");

const isValidGpsPosition = (position) =>
  Array.isArray(position) &&
  position.length === 2 &&
  position.every((value) => Number.isFinite(Number(value)));

const UpdateToolPage = () => {
  const { id } = useParams();
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const instruments = useSelector((state) => state.instruments?.data?.data);
  const sensors = useSelector((state) => state.sensors?.data?.data);
  const selectedTool = sensors?.find((tool) => tool?.id === id);
  const selectedInstrument = instruments?.find((i) => i?.id === id);
  const selectedGateway = instruments?.find((g) => g?.id === id);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      nickname: selectedTool?.nickname || selectedGateway?.nickname || "",
      devEUI: selectedTool?.devEUI || selectedGateway?.devEUI || "",
      sensor_type: selectedTool?.sensor_type?.id || "",
      update_frequency: selectedTool?.update_frequency || "",
      field_id: getAssignedFieldId(selectedTool),
      appEUI: selectedTool?.appEUI || "",
      appKey: selectedTool?.appKey || "",
      gps_position: selectedTool?.gps_position || null,
    },
  });

  const fields = useSelector((state) => state.fields?.data?.data);

  const accessToken = useSelector((state) => state.auth?.user?.access_token);
  const { success, error } = useSelector((state) => state.sensor);
  const gatewaySuccess = useSelector((state) => state.gateway?.success);
  const gatewayError = useSelector((state) => state.gateway?.error);
  const updateFrequencies = useSelector(
    (state) => state.updateFrequencies?.data?.data
  );
  const sensorTypes = useSelector((state) => state.sensorTypes?.data?.data);

  const [formData, setFormData] = useState("");
  const [openDialog, setOpenDialog] = useState(false);

  const isSensor = selectedInstrument?.instrument_type === "Sensor";
  const isGateway = selectedInstrument?.instrument_type === "Gateway";

  const handleFormDataChange = useCallback((formData) => {
    setFormData(formData);
  }, []);

  const onSubmit = () => {
    const currentFieldId = getAssignedFieldId(selectedTool);
    const submittedFieldId = formData?.field_id || currentFieldId;
    const isFieldChanged = !isSameId(submittedFieldId, currentFieldId);
    const currentGpsPosition = isValidGpsPosition(formData?.gps_position)
      ? formData.gps_position
      : null;
    const gpsPosition = currentGpsPosition || selectedTool?.gps_position;

    const formattedData = {
      nickname: formData.nickname,
      update_frequency: formData.update_frequency,
      field: submittedFieldId || "",
      devEUI: selectedTool?.devEUI || selectedGateway.devEUI,
      appEUI: selectedTool?.appEUI,
      appKey: selectedTool?.appKey,
      sensor_type: selectedTool?.sensor_type?.id,
      gps_position:
        !isFieldChanged && isValidGpsPosition(gpsPosition) ? gpsPosition : null,
    };
    if (isGateway) {
      dispatch(
        updateGatewayStart({
          data: formattedData,
          gatewayId: id,
          accessToken: accessToken,
        })
      );
    } else {
      dispatch(
        updateSensorStart({
          data: formattedData,
          sensorId: id,
          accessToken: accessToken,
        })
      );
    }
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  const handleConfirmDialog = () => {
    if (isGateway) {
      dispatch(deleteGatewayStart({ gatewayId: id, accessToken: accessToken }));
    }
    if (isSensor) {
      dispatch(deleteSensorStart({ sensorId: id, accessToken: accessToken }));
    }
    setOpenDialog(false);
    navigate("/tools");
    dispatch(resetDeleteGatewayState());
    dispatch(resetDeleteSensorState());
  };
  const handleResetUpdate = () => {
    dispatch(resetUpdateSensorState());
    dispatch(resetUpdateGatewayState());
  };

  useEffect(() => {
    dispatch(fetchFieldsStart({ accessToken }));
    dispatch(fetchUpdateFrequenciesStart({ accessToken }));
    dispatch(fetchSensorTypesStart({ accessToken }));
    dispatch(fetchSensorsStart({ accessToken }));
  }, [dispatch, accessToken]);

  return (
    <Box
      width="100%"
      height="100%"
      display="flex"
      flexDirection="column"
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Paper
        sx={{
          paddingX: isMobile ? 2 : 20,
          paddingY: isMobile ? 2 : 5,
          width: "100%",
          height: "100%",
          overflow: "auto",
        }}
        elevation={0}
      >
        <Box display="flex" width="100%" justifyContent="end" mb={2}>
          <FormActionButton
            startIcon={<Delete />}
            color="error"
            label={`Elimina ${isSensor ? "sensore" : "gateway"}`}
            onClick={() => setOpenDialog(true)}
          />
        </Box>
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography fontWeight="bold" fontSize="14px">
            {`Info ${isSensor ? "sensore" : "gateway"}`}
          </Typography>
          {isGateway && (
            <UpdateGatewayForm
              onFormDataChange={handleFormDataChange}
              control={control}
              errors={errors}
            />
          )}
          {isSensor && (
            <UpdateSensorForm
              onFormDataChange={handleFormDataChange}
              control={control}
              errors={errors}
              sensorTypes={sensorTypes}
              frequencyUpdateRanges={updateFrequencies}
              fields={fields}
            />
          )}
        </Box>
        <Box display="flex" flexDirection="column" gap={2} mt={2}>
          <Typography fontWeight="bold" fontSize="14px">
            Link sul campo
          </Typography>
          <LinkButton
            icon={<MapOutlined />}
            label="Vedi link GPS su mappa"
            disabled
          />
        </Box>
      </Paper>
      <SubmitButton label={`Aggiorna ${isSensor ? "sensore" : "gateway"}`} />
      <CustomDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={dialogMessages.deleteTool.title}
        description={dialogMessages.deleteTool.description}
        onConfirm={handleConfirmDialog}
      />
      {(success || gatewaySuccess) && (
        <CustomAlert
          severity="success"
          message={`${
            isSensor ? "Sensore" : "Gateway"
          } aggiornato con successo`}
          onClose={handleResetUpdate}
        />
      )}
      {(error || gatewayError) && (
        <CustomAlert
          severity="error"
          message={getErrorMessage(error || gatewayError, {
            fallback: `Errore durante l'aggiornamento del ${
              isSensor ? "sensore" : "gateway"
            }`,
          })}
          onClose={handleResetUpdate}
        />
      )}
    </Box>
  );
};

export default UpdateToolPage;
